import React from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { cancelGolfSpotReservation } from '../../../api/golf';

const GolfReservationDeleteModal = ({
  open,
  onClose,
  reservationId,
  onSuccess,
  onLoadingStart,
  onLoadingEnd,
  isCancelMode = false,
}) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();

  const cancelReservationMutation = useMutation({
    mutationFn: ({ reservationId }) => cancelGolfSpotReservation(user.token, reservationId),
    onSuccess: () => {
      toast.success(isCancelMode ? '예약이 취소되었습니다.' : '이용이 종료되었습니다.');
      // 로딩 종료 콜백 호출
      if (onLoadingEnd) {
        onLoadingEnd();
      }
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      toast.error(isCancelMode ? '예약 취소 중 오류가 발생했습니다.' : '이용 종료 중 오류가 발생했습니다.');
      // 에러 발생 시에도 로딩 종료
      if (onLoadingEnd) {
        onLoadingEnd();
      }
    },
  });

  const handleConfirm = () => {
    if (reservationId) {
      // 로딩 시작 콜백 호출
      if (onLoadingStart) {
        onLoadingStart();
      }
      cancelReservationMutation.mutate({
        reservationId: reservationId,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="end-confirm-modal-title"
      aria-describedby="end-confirm-modal-description">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '465px',
          height: 'auto',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          p: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              mb: '24px',
            }}>
            <Typography component="span" sx={{ fontSize: '24px', fontWeight: '600', color: '#000' }}>
              {isCancelMode ? '예약을 ' : '이용을 '}&nbsp;
            </Typography>
            <Typography component="span" sx={{ fontSize: '24px', fontWeight: '600', color: '#ee735a' }}>
              {isCancelMode ? '취소' : '종료'}
            </Typography>
            <Typography component="span" sx={{ fontSize: '24px', fontWeight: '600', color: '#000' }}>
              하시겠습니까?
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '16px', fontWeight: '400', color: '#737373', mb: '24px' }}>
            {isCancelMode
              ? '예약을 취소하시면 현재 예약된 내역이 삭제되며 복구가 불가합니다.'
              : '이용을 취소하시면 현재 사용중인 이용권이 취소됩니다.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', mt: '8px' }}>
          <Button
            sx={{
              backgroundColor: '#D9D9D9',
              width: '120px',
              height: '48px',
              borderRadius: '16px',
              fontSize: '20px',
              color: '#000',
              '&:hover': { backgroundColor: '#D9D9D9' },
            }}
            onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            sx={{
              backgroundColor: '#EE735A',
              width: '240px',
              height: '48px',
              borderRadius: '16px',
              fontSize: '20px',
              color: '#fff',
              '&:hover': { backgroundColor: '#EE735A' },
            }}
            onClick={handleConfirm}
            disabled={cancelReservationMutation.isPending}>
            {isCancelMode ? '예약취소' : t('End')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default GolfReservationDeleteModal;
